/**
 * Camada do detalhe: desenha, sobre a imagem inteira de uma camada GOES (a
 * base), os blocos à vista do MESMO quadro que a base mostra, no nível pedido
 * (3600, 7200 ou 14400 px). Quem decide o quadro é o `GoesLayerManager` — o
 * detalhe nunca está num horário diferente da base.
 *
 * Pirâmide de reserva: onde o bloco do nível pedido ainda não chegou (zoom que
 * acabou de trocar de nível) ou não existe (404: de noite não há 14400), vale o
 * que houver decodificado do MESMO quadro noutro nível — por baixo, do menor
 * para o maior, e o nível pedido por cima. Só na falta de todos aparece a base.
 * Os de reserva só são desenhados (e fixados no cache) enquanto cobrem um
 * buraco; com o nível pedido completo, saem.
 *
 * Os blocos vão direto no canvas da camada, sem mosaico intermediário: um
 * mosaico da vista inicial em tela 2× passaria do limite de canvas do Safari
 * (~16,7 Mpx). Cada bloco é desenhado só no miolo; a sobra de 16 px do
 * vizinho, que vem no arquivo, é lida pela suavização — sem emendas.
 */
import type { FrameState } from 'ol/Map';
import Layer from 'ol/layer/Layer';
import { containsExtent, type Extent } from 'ol/extent';
import { fromUserExtent } from 'ol/proj';
import CanvasLayerRenderer from 'ol/renderer/canvas/Layer';
import { apply as applyTransform } from 'ol/transform';
import { imageExtentFor } from '../map/imageGeometry';
import { blocosDe, extensaoDoBloco, faixaCorrespondente, faixaVisivel, mioloDoBloco, urlDoBloco, type Faixa, type Nivel } from './blocos';
import { blocoCache } from './cache';
import { niveisDo } from './saude';

export interface QuadroDetalhe {
  produto: string;
  tempo: number;
  nivel: Nivel;
}

interface BlocoNaTela {
  url: string;
  imagem: CanvasImageSource & { width: number; height: number };
  sx: number;
  sy: number;
  sw: number;
  sh: number;
  extensao: Extent;
}

const mesmoQuadro = (a: QuadroDetalhe | null, b: QuadroDetalhe | null) =>
  a === b || (!!a && !!b && a.produto === b.produto && a.tempo === b.tempo && a.nivel === b.nivel);

class DetalheRenderer extends CanvasLayerRenderer<DetalheLayer> {
  private blocos: BlocoNaTela[] = [];

  override prepareFrame(frameState: FrameState): boolean {
    this.blocos = frameState.extent ? this.getLayer().blocosNaTela(frameState.extent) : [];
    return this.blocos.length > 0;
  }

  override renderFrame(frameState: FrameState, target: HTMLElement | null): HTMLElement {
    const layerState = frameState.layerStatesArray[frameState.layerIndex];
    this.prepareContainer(frameState, target as HTMLElement);
    const context = this.getRenderContext(frameState);

    let recortado = false;
    if (layerState.extent) {
      const recorte = fromUserExtent(layerState.extent, frameState.viewState.projection);
      if (frameState.extent && !containsExtent(recorte, frameState.extent)) {
        this.clipUnrotated(context, frameState, recorte);
        recortado = true;
      }
    }

    // Mesma suavização da base (FrameLayer.ts): ampliando, sem aspecto quadriculado.
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    this.preRender(context, frameState);
    const opacidade = layerState.opacity;
    if (opacidade !== 1) {
      context.save();
      context.globalAlpha = opacidade;
    }
    const pixel = (x: number, y: number) => {
      const p = [x, y];
      applyTransform(frameState.coordinateToPixelTransform, p);
      applyTransform(this.inversePixelTransform, p);
      return p;
    };
    for (const b of this.blocos) {
      // Bordas arredondadas pela MESMA conta nos dois vizinhos: sem fresta nem sobreposição.
      const [x0, y0] = pixel(b.extensao[0], b.extensao[3]);
      const [x1, y1] = pixel(b.extensao[2], b.extensao[1]);
      const dx = Math.round(x0);
      const dy = Math.round(y0);
      context.drawImage(b.imagem, b.sx, b.sy, b.sw, b.sh, dx, dy, Math.round(x1) - dx, Math.round(y1) - dy);
    }
    if (opacidade !== 1) context.restore();
    this.postRender(context, frameState);
    if (recortado) context.restore();
    return this.container;
  }
}

export class DetalheLayer extends Layer {
  private quadro: QuadroDetalhe | null = null;
  /** Blocos desenhados agora: fixados no cache enquanto estão na tela. */
  private fixados = new Set<string>();
  private redesenhoPendente = false;
  private readonly pararCache: () => void;

  constructor(className: string) {
    super({ className });
    // Bloco novo pronto do quadro na tela: redesenha (no máximo uma vez por quadro de vídeo).
    this.pararCache = blocoCache.subscribe(() => {
      if (!this.quadro || this.redesenhoPendente) return;
      this.redesenhoPendente = true;
      requestAnimationFrame(() => {
        this.redesenhoPendente = false;
        this.changed();
      });
    });
  }

  /** Quadro a desenhar (o que a base mostra), ou null para só a base. */
  setQuadro(quadro: QuadroDetalhe | null): void {
    if (mesmoQuadro(this.quadro, quadro)) return;
    this.quadro = quadro;
    if (!quadro) this.fixar([]);
    this.changed();
  }

  getQuadro(): QuadroDetalhe | null {
    return this.quadro;
  }

  /**
   * Blocos à vista do quadro que já estão decodificados, na ordem de desenho:
   * primeiro os de reserva (outros níveis, só onde falta o pedido), por cima
   * os do nível pedido.
   */
  blocosNaTela(vista: Extent): BlocoNaTela[] {
    const q = this.quadro;
    const out: BlocoNaTela[] = [];
    if (q) {
      const ativos: BlocoNaTela[] = [];
      const buracos = new Set<string>();
      const faixa = faixaVisivel(vista, imageExtentFor(q.produto, q.nivel), q.nivel);
      if (faixa) {
        for (const [l, c] of blocosDe(faixa)) {
          const b = this.bloco(q, q.nivel, l, c);
          if (b) ativos.push(b);
          else buracos.add(`${l}_${c}`);
        }
      }
      if (buracos.size) {
        const cobreBuraco = (f: Faixa) => {
          for (const [l, c] of blocosDe(f)) if (buracos.has(`${l}_${c}`)) return true;
          return false;
        };
        for (const nivel of [...niveisDo(q.produto)].sort((a, b) => a - b)) {
          if (nivel === q.nivel) continue;
          const f = faixaVisivel(vista, imageExtentFor(q.produto, nivel), nivel);
          if (!f) continue;
          for (const [l, c] of blocosDe(f)) {
            if (!cobreBuraco(faixaCorrespondente(nivel, q.nivel, l, c))) continue;
            const b = this.bloco(q, nivel, l, c);
            if (b) out.push(b);
          }
        }
      }
      out.push(...ativos);
    }
    this.fixar(out.map((b) => b.url));
    return out;
  }

  private bloco(q: QuadroDetalhe, nivel: Nivel, l: number, c: number): BlocoNaTela | null {
    const url = urlDoBloco(q.produto, q.tempo, nivel, l, c);
    const imagem = blocoCache.get(url);
    if (!imagem) return null;
    const m = mioloDoBloco(nivel, l, c);
    return { url, imagem, sx: m.sx, sy: m.sy, sw: m.largura, sh: m.altura, extensao: extensaoDoBloco(imageExtentFor(q.produto, nivel), nivel, l, c) };
  }

  private fixar(urls: string[]): void {
    const novos = new Set(urls);
    for (const u of novos) if (!this.fixados.has(u)) blocoCache.pin(u);
    for (const u of this.fixados) if (!novos.has(u)) blocoCache.unpin(u);
    this.fixados = novos;
  }

  protected override createRenderer(): DetalheRenderer {
    return new DetalheRenderer(this);
  }

  override disposeInternal(): void {
    this.pararCache();
    this.fixar([]);
    super.disposeInternal();
  }
}
